import type {
	Activity,
	ScriptItem,
	SessionChunk,
} from 'features/script/script-model'

export type AgendaStep = {
	chunk: SessionChunk
	item: ScriptItem
	activity?: Activity
}

export type AgendaClock = {
	now?: {
		title: string
		notes: string
		plannedStart: Date
		/** Unknown for the last item of a chunk without a real end time. */
		plannedEnd?: Date
		/** Against the plan; negative when over time. */
		minutesLeft?: number
		activity?: Activity
	}
}

const MINUTE_MS = 60 * 1000

/**
 * The chunk's planned end. The backend sends no end time yet and
 * `script-redux.ts` fills in 23:59 (its FIXME) - that is no plan, so it counts
 * as unknown.
 */
export const chunkEnd = (chunk: SessionChunk): Date | undefined => {
	const { hours, minutes } = chunk.endTime
	if (hours === 23 && minutes === 59) {
		return undefined
	}
	const end = new Date(chunk.startDate)
	end.setHours(hours, minutes, 0, 0)
	return end
}

/** The current item's planned end: the next item's start, or the chunk's end. */
const plannedEndOf = ({ chunk, item }: AgendaStep): Date | undefined => {
	const index = chunk.items.indexOf(item)
	const following = index >= 0 ? chunk.items[index + 1] : undefined
	return following ? following.startTime : chunkEnd(chunk)
}

/**
 * The step being run and how it stands against the plan. There is no
 * "actually started at" in the script, so time is measured against the
 * planned times only. (What comes next is the agenda list, `agenda-outline`.)
 */
export const agendaClock = (
	current: AgendaStep | undefined,
	now: Date
): AgendaClock => {
	const plannedEnd = current ? plannedEndOf(current) : undefined
	return {
		now: current
			? {
					title: current.item.title,
					notes: current.item.description,
					plannedStart: current.item.startTime,
					plannedEnd,
					minutesLeft: plannedEnd
						? Math.ceil((plannedEnd.getTime() - now.getTime()) / MINUTE_MS)
						: undefined,
					activity: current.activity,
			  }
			: undefined,
	}
}
